import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createSession } from '@/api/dataroom';
import { normalizeError } from '@/api/errors';
import { getSessionToken, setExpiredHandler, setSessionToken } from '@/api/sessionStore';
import { SUPPORTED_LOCALES } from '@/i18n/I18nContext';
import { useI18n } from '@/i18n/useI18n';
import { SessionContext, type SessionContextValue, type SessionState } from './SessionContext';

/**
 * Owns the data-room session.
 *
 * SECURITY POSTURE (this is an external PII surface):
 *  - The session bearer lives ONLY in the in-memory sessionStore — never localStorage/sessionStorage.
 *  - The invite token is read from the URL (`?t=`) and traded for a session. It is kept in the URL so
 *    a hard refresh (which wipes the in-memory bearer) can re-exchange it; if the token is gone we
 *    show a "reopen your link" gate rather than a blank authenticated view.
 *  - A 401 anywhere rejects closed → the session is dropped and the "expired" gate shows.
 *  - The client never sends a tenant/hub/entity id — the bearer carries tenancy (server-bound).
 *
 * CONTRACT NOTE for Track A: refresh-recovery re-exchanges the invite. That requires the exchange to
 * be idempotent for a still-valid invite. The contract's "single-use → 410 GONE" is rendered as the
 * terminal "already used" gate; if the backend strictly single-uses on first exchange, a refresh
 * after proceeding will land on that gate (acceptable per the brief's stated fallback).
 */
export function SessionProvider({ children }: { children: ReactNode }) {
  const { setLocale } = useI18n();

  // Read the invite token once — it is stable for the tab's lifetime.
  const inviteToken = useMemo(
    () => new URLSearchParams(window.location.search).get('t'),
    [],
  );

  const [state, setState] = useState<SessionState>(() =>
    inviteToken
      ? { status: 'exchanging', session: null, error: null }
      : { status: 'no-link', session: null, error: null },
  );

  // Guards against double-exchange (React 18 StrictMode double-invokes effects in dev; a single-use
  // invite must be exchanged at most once per token).
  const exchangedRef = useRef(false);
  const localeSetRef = useRef(false);

  const doExchange = useCallback(
    async (token: string) => {
      setState({ status: 'exchanging', session: null, error: null });
      try {
        const session = await createSession(token);
        setSessionToken(session.sessionToken); // in memory ONLY
        // Adopt the session's locale once — but only if we support it, so an unexpected server value
        // can never break the document's lang/dir. Unsupported → keep the detected locale.
        if (!localeSetRef.current && (SUPPORTED_LOCALES as string[]).includes(session.locale)) {
          setLocale(session.locale);
          localeSetRef.current = true;
        }
        setState({ status: 'active', session, error: null });
      } catch (err) {
        const e = normalizeError(err);
        setState({
          status: e.kind === 'GONE' ? 'gone' : 'error',
          session: null,
          error: e,
        });
      }
    },
    [setLocale],
  );

  // Mount: exchange the invite (once), or show the no-link gate.
  useEffect(() => {
    if (getSessionToken()) return; // an active session already lives in memory (SPA nav)
    if (!inviteToken) {
      setState({ status: 'no-link', session: null, error: null });
      return;
    }
    if (exchangedRef.current) return;
    exchangedRef.current = true;
    void doExchange(inviteToken);
  }, [inviteToken, doExchange]);

  // Reject-closed: a 401 during app use drops the session and shows the expired gate.
  useEffect(() => {
    setExpiredHandler(() => {
      setSessionToken(null);
      setState((s) =>
        s.status === 'active' ? { status: 'expired', session: null, error: null } : s,
      );
    });
    return () => setExpiredHandler(null);
  }, []);

  const retry = useCallback(() => {
    if (inviteToken) {
      void doExchange(inviteToken);
    } else {
      setState({ status: 'no-link', session: null, error: null });
    }
  }, [inviteToken, doExchange]);

  const value = useMemo<SessionContextValue>(() => ({ ...state, retry }), [state, retry]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}
